import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Language } from "../../App";
import { toast } from "react-toastify";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { CHAT, NEW_PROFILE_DOCTOR } from "../../constants/RoutesUrl";
import { getProfileRoute } from "../../utils/profileNavigator";
import { IoClose, IoCalendarOutline, IoTimeOutline, IoChatbubbleOutline, IoOpenOutline, IoVideocamOutline } from "react-icons/io5";
import { PiFilePdf } from "react-icons/pi";
import { FaChevronDown, FaArrowRight } from "react-icons/fa";
import moment from "moment";
import ModernDatePicker from "../common/ModernDatePicker";
import ModernTimePicker from "../common/ModernTimePicker";
import { MdOutlineSms } from "react-icons/md";
import zoom from "../../assets/images/zoom.svg";
import { LiaVideoSolid } from "react-icons/lia";
import { _imagesURL } from "../../constants/API";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { Users3DIcon, Clock3DIcon, Calendar3DIcon } from "../agenda/common/Agenda3DIcons";

// A function (not a static array) so the "minutes" word stays in sync with
// the same word used to prefill `duration` from mentee.durationMinutes below
// — both need to build the exact same string for FieldSelect to show the
// right option as selected.
function getDurationOptions(minutesWord) {
  return [15, 30, 45, 60, 90].map((n) => `${n} ${minutesWord}`);
}

// The handful of zones doctors on this platform actually need — covering
// Europe (this app's home market), US east/west, and Karachi/Dubai (where
// most of the current mentor/mentee base is) — instead of every IANA zone.
const TIMEZONES = ["UTC", "Europe/Madrid", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Karachi", "Asia/Dubai"];

// "Asia/Karachi" -> "Asia/Karachi" — just the zone name, no GMT/UTC offset.
function formatTimezoneLabel(tz) {
  return tz.replace(/_/g, " ");
}

const TIMEZONE_OPTIONS = TIMEZONES.map(formatTimezoneLabel);

function CheckSvg() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path
        d="M1 4L3.5 6.5L9 1"
        stroke="#00BADA"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// A native <select>'s open direction (up vs down) is decided by the
// browser itself based on available viewport space — with ~400 timezone
// options that list almost never fits below, so it was opening upward and
// covering the field. This custom dropdown is positioned explicitly below
// its trigger and scrolls internally instead, so it always opens down.
function FieldSelect({ value, onChange, options, placeholder, error, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div>
      <div className="position-relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          disabled={disabled}
          className="d-flex align-items-center justify-content-between w-100"
          style={{
            border: `1.5px solid ${error ? "#EF4444" : isOpen ? "#00BADA" : "#DCE5EE"}`,
            borderRadius: "12px",
            padding: "10px 14px",
            fontSize: "13.5px",
            fontWeight: 500,
            color: value ? "#0F1A2B" : "#94A3B8",
            background: disabled ? "#F8FAFC" : "#FFFFFF",
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.7 : 1,
            boxShadow: isOpen ? "0 0 0 3px rgba(0, 186, 218, 0.15)" : "0 1px 2px rgba(0, 0, 0, 0.03)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <span className="text-truncate" style={{ minWidth: 0 }}>{value || placeholder}</span>
          <FaChevronDown
            size={11}
            color={isOpen ? "#00BADA" : "#64748B"}
            style={{
              flexShrink: 0,
              marginLeft: "8px",
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease, color 0.2s ease",
            }}
          />
        </button>

        {isOpen && (
          <div
            className="position-absolute"
            style={{
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              zIndex: 35,
              maxHeight: "220px",
              overflowY: "auto",
              background: "#FFFFFF",
              border: "1.5px solid #E2E8F0",
              borderRadius: "14px",
              boxShadow: "0 12px 32px rgba(15, 26, 43, 0.14), 0 2px 6px rgba(0, 0, 0, 0.04)",
              padding: "4px",
            }}
          >
            {options.map((opt) => {
              const isSelected = opt === value;
              return (
                <div
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                  }}
                  className="text-truncate"
                  style={{
                    padding: "9px 12px",
                    fontSize: "13px",
                    fontWeight: isSelected ? 700 : 500,
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: isSelected ? "#0891B2" : "#0F1A2B",
                    background: isSelected ? "linear-gradient(135deg, rgba(0, 186, 218, 0.12) 0%, rgba(2, 132, 199, 0.08) 100%)" : "transparent",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "#F0FBFD";
                      e.currentTarget.style.color = "#00BADA";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#0F1A2B";
                    }
                  }}
                >
                  {opt}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {error && (
        <p className="fs-11 mb-0 mt-1" style={{ color: "#EF4444", fontWeight: 500 }}>
          {error}
        </p>
      )}
    </div>
  );
}

function mentorshipSubtitle(mentee, lang) {
  if (!mentee) return lang?.mentorship_default_subtitle || "Mentorship Session";
  const caseReview = lang?.mentorship_case_review || "Case Review Mentorship";
  if (mentee.specialty) return `${mentee.specialty} ${caseReview}`;
  return caseReview;
}

// Reconstructs the actual saved session time from the raw fields
// MentorshipRequests.jsx's mapActivity passes through (startTimestamp /
// startDate+startTime) — used to pre-fill the reschedule view with what was
// really booked, instead of re-parsing the formatted `meta` display string.
function getMenteeSessionMoment(mentee) {
  if (mentee?.startTimestamp) {
    const m = moment.unix(Number(mentee.startTimestamp));
    if (m.isValid()) return m;
  }
  if (mentee?.startDate && mentee?.startTime) {
    const m = moment(
      `${mentee.startDate} ${mentee.startTime}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD h:mm A"]
    );
    if (m.isValid()) return m;
  }
  return null;
}

// Only the parts validate()/handleConfirm actually use for each field —
// date compares by calendar day (its H/m carried over from the parsed
// moment are irrelevant), startTime by hour+minute (its Y/M/D are
// irrelevant) — so picking the same day/time again after nudging the
// picker and back doesn't read as "changed".
function isSameCalendarDate(a, b) {
  const aValid = a?.isValid?.();
  const bValid = b?.isValid?.();
  if (!aValid || !bValid) return aValid === bValid;
  return a.isSame(b, "day");
}
function isSameClockTime(a, b) {
  const aValid = a?.isValid?.();
  const bValid = b?.isValid?.();
  if (!aValid || !bValid) return aValid === bValid;
  return a.hour() === b.hour() && a.minute() === b.minute();
}

function MentorShipSchedule({ isOpen, toggle, mentee }) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const queryClient = useQueryClient();
  const durationOptions = getDurationOptions(lang?.minutes || "minutes");
  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState("");
  const [timezone, setTimezone] = useState("");
  const [notifyViaChat, setNotifyViaChat] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Snapshot of what a "Sessions Confirmed" item was actually prefilled
  // with, taken once when the modal opens — Reschedule Session stays
  // disabled until at least one field diverges from this, so hitting the
  // API is only ever a real reschedule, not a no-op resubmit of the same
  // date/time/duration/timezone.
  const initialScheduleRef = useRef(null);

  const isConfirmed = mentee?.status === "Sessions Confirmed";
  // A live session — same prefilled/read-only shape as a confirmed one
  // (there's a real booked time and Zoom link to show), but with no
  // reschedule action: the call is already happening, there's nothing left
  // to confirm or send to the mentee.
  const isInProgress = mentee?.status === "Mentorship in Progress";

  // Check if session duration has ended
  const sessionMoment = getMenteeSessionMoment(mentee);
  const durationMins = Number(mentee?.durationMinutes) || 0;
  const endSeconds =
    sessionMoment && durationMins ? sessionMoment.unix() + durationMins * 60 : null;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isDurationCompleted =
    !mentee?.isCompleted &&
    (isConfirmed || isInProgress) &&
    endSeconds !== null &&
    nowSeconds >= endSeconds;

  // Resets to blank for a fresh "Awaiting Mentor Response" item, or
  // pre-fills with the already-booked time for a "Sessions Confirmed" /
  // "Mentorship in Progress" one — runs whenever the modal opens for a
  // (possibly different) mentee, so fields never carry over stale values
  // from the last person scheduled.
  useEffect(() => {
    if (!isOpen || !mentee) return;
    if (isConfirmed || isInProgress) {
      const parsed = getMenteeSessionMoment(mentee);
      const durationStr = mentee.durationMinutes ? `${mentee.durationMinutes} ${lang?.minutes || "minutes"}` : "";
      const timezoneStr = mentee.timezone ? formatTimezoneLabel(mentee.timezone) : "";
      setDate(parsed);
      setStartTime(parsed);
      setDuration(durationStr);
      setTimezone(timezoneStr);
      initialScheduleRef.current = isConfirmed
        ? { date: parsed, startTime: parsed, duration: durationStr, timezone: timezoneStr }
        : null;
    } else {
      setDate(null);
      setStartTime(null);
      setDuration("");
      setTimezone("");
      initialScheduleRef.current = null;
    }
    setNotifyViaChat(false);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mentee]);

  // Same curtain-modal pattern as ApplyMentorShip.jsx / NewCourseDetail2.0.jsx's
  // VideoModal — plain backdrop + content divs animated with @keyframes,
  // not reactstrap's Modal/Fade, so open and close are both immediate and
  // symmetric instead of gated behind a JS timeout.
  const [isClosing, setIsClosing] = useState(false);
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!mentee) return null;
  if (!isOpen && !isClosing) return null;

  const handleGoToProfile = () => {
    const identifier = mentee.user_name || mentee.profileId || mentee.id;
    if (!identifier) return;
    closeThen(() => {
      navigate(
        getProfileRoute({
          ...mentee,
          id: mentee.profileId || mentee.id,
          user_name: mentee.user_name,
          user_type: mentee.user_type || mentee.type || "doctor",
        })
      );
    });
  };

 
  const handleGoToChat = () => {
    if (!mentee.profileId) return;
    const loginId = JSON.parse(localStorage.getItem("SplashData") || "null")?.user?.id;
    closeThen(() =>
      navigate(CHAT, {
        state: {
          from: loginId,
          to: mentee.profileId,
          group_id: 0,
          user: {
            user_id: mentee.profileId,
            name: mentee.name,
            image: "",
          },
        },
      })
    );
  };

  
  const validate = () => {
    const newErrors = {};
    if (!date || !date.isValid?.()) newErrors.date = lang?.mentorship_error_date || "Please select the date.";
    if (!startTime || !startTime.isValid?.()) newErrors.startTime = lang?.mentorship_error_time || "Please select the time.";

    // Catches a past date on its own (independent of startTime) — the
    // picker's own minDate already blocks picking one, but this is the
    // fallback if a past date ever ends up in state some other way, and it
    // points the error at the date field instead of only surfacing once
    // startTime also happens to be set.
    if (date?.isValid?.() && date.isBefore(moment(), "day")) {
      newErrors.date =
        lang?.mentorship_error_past_date ||
        "Please select today's date, then choose a future date.";
    }

    if (date?.isValid?.() && startTime?.isValid?.()) {
      const combined = moment(date).set({
        hour: startTime.hour(),
        minute: startTime.minute(),
        second: 0,
        millisecond: 0,
      });
      const isToday = date.isSame(moment(), "day");
      const minSelectable = isToday ? moment().add(10, "minutes") : moment();
      if (combined.isBefore(minSelectable)) {
        newErrors.startTime = isToday
          ? lang?.mentorship_error_min_10_minutes || "For today's date, start time must be at least 10 minutes from current time."
          : lang?.mentorship_error_past_time || "Please select a future time.";
      }
    }
    if (!duration) newErrors.duration = lang?.mentorship_error_duration_select || "Please select the duration.";
    if (!timezone) newErrors.timezone = lang?.mentorship_error_timezone || "Please select the timezone.";
    // Only asked for on a fresh request — reschedule has no checkbox to check.
    if (!isConfirmed && !notifyViaChat) {
      newErrors.notifyViaChat = lang?.mentorship_error_notify_checkbox || "Please check this box to continue.";
    }
    return newErrors;
  };

  
  const isRescheduleUnchanged =
    isConfirmed &&
    !!initialScheduleRef.current &&
    isSameCalendarDate(date, initialScheduleRef.current.date) &&
    isSameClockTime(startTime, initialScheduleRef.current.startTime) &&
    duration === initialScheduleRef.current.duration &&
    timezone === initialScheduleRef.current.timezone;

  const handleConfirm = async () => {
    // Belt-and-braces alongside the button's own `disabled` below — nothing
    // to send to the API when reschedule fields are still exactly what was
    // already booked.
    if (isRescheduleUnchanged) return;
    const newErrors = validate();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const startDateStr = date.format("YYYY-MM-DD");
      const startTimeStr = startTime.format("HH:mm:ss");
      const startTimestamp = moment(
        `${startDateStr} ${startTimeStr}`,
        "YYYY-MM-DD HH:mm:ss"
      ).unix();
      // "90 minutes" -> 90 (activity.duration elsewhere in the API is a
      // plain minute count, not this dropdown's display label).
      const durationMinutes = parseInt(duration, 10) || 0;

      // apiAdmin's interceptor (src/services/api.js) already attaches this
      // same Bearer token to every request — set explicitly here too so the
      // header is visibly present on this specific call, matching how
      // getSplashData() does it in that same file.
      const token = JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.token;
      const res = await apiAdmin.post(
        "user/mentor/schedule",
        {
          request_id: mentee.id,
          start_date: startDateStr,
          start_time: startTimeStr,
          start_timestamp: startTimestamp,
          duration: durationMinutes,
          timezone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        const template = isConfirmed
          ? lang?.mentorship_rescheduled_toast || "Session with {{name}} rescheduled to {{date}} at {{time}}."
          : lang?.mentorship_scheduled_toast || "Session scheduled with {{name}} on {{date}} at {{time}}.";
        toast.success(
          template
            .replace("{{name}}", mentee.name)
            .replace("{{date}}", date.format("DD MMM YYYY"))
            .replace("{{time}}", startTime.format("hh:mm A"))
        );
        // Refetch MentorshipRequests' list so this row picks up its new
        // status instead of still showing "Awaiting" until a manual refresh.
        queryClient.invalidateQueries(["mentorshipActivity"]);
        closeThen();
      } else {
        toast.error(getApiErrorMessage(res.data, lang?.mentorship_error_schedule || "Couldn't schedule this session. Please try again."));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data, lang?.mentorship_error_schedule || "Couldn't schedule this session. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!mentee?.id) return;
    setCompleting(true);
    try {
      const token = JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.token;
      const res = await apiAdmin.post(
        "user/mentor/complete",
        { request_id: mentee.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        toast.success(
          lang?.mentorship_session_completed_toast || "Mentorship session completed successfully."
        );
        queryClient.invalidateQueries(["mentorshipActivity"]);
        closeThen();
      } else {
        toast.error(
          getApiErrorMessage(
            res.data,
            lang?.mentorship_error_complete || "Couldn't complete this session. Please try again."
          )
        );
      }
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className={`mss-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Same curtain-modal pattern as ApplyMentorShip.jsx & MentorshipApplicationModal.jsx */
        .mss-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
          animation: mssBackdropFadeIn 0.8s ease-out forwards;
        }
        .mss-curtain-backdrop.closing {
          animation: mssBackdropFadeOut 0.8s ease-out forwards;
        }
        .mss-curtain-content {
          position: relative;
          width: 100%;
          max-width: 550px;
          margin: auto;
          background: #FFFFFF;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          transform: translateY(-100vh);
          animation: mssCurtainOpen 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
          overflow: visible;
        }
        .mss-curtain-content.closing {
          animation: mssCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes mssBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes mssBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes mssCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes mssCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mss-curtain-backdrop, .mss-curtain-content {
            animation: none !important;
          }
        }

        /* Modern Glass Close Button */
        .mss-close-btn {
          position: absolute;
          top: 18px;
          right: 20px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid #BCEAF2;
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0E7490;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 30;
        }
        .mss-close-btn:hover {
          background: #00BADA;
          color: #FFFFFF;
          border-color: #00BADA;
          transform: rotate(90deg) scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 186, 218, 0.35);
        }

        /* Mentee Card */
        .mss-mentee-card {
          background: linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%);
          border: 1.5px solid #B4E8F2;
          border-radius: 16px;
          padding: 12px 16px;
          box-shadow: 0 2px 10px rgba(0, 186, 218, 0.08);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .mss-mentee-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 120px;
          height: 120px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%);
          pointer-events: none;
        }
        .mss-mentee-card:hover {
          border-color: #00BADA;
          box-shadow: 0 4px 16px rgba(0, 186, 218, 0.18);
        }

        .mss-avatar-wrap {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          padding: 2px;
          background: linear-gradient(135deg, #00F0FF 0%, #00BADA 50%, #0284C7 100%);
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.3);
          flex-shrink: 0;
        }
        .mss-avatar-img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          border: 2px solid #FFFFFF;
        }

        .mss-chat-btn {
          width: 38px;
          height: 34px;
          border-radius: 12px;
          background: #FFFFFF;
          border: 1.5px solid #B4E8F2;
          box-shadow: 0 2px 6px rgba(0, 186, 218, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .mss-chat-btn:hover {
          border-color: #00BADA;
          background: #F0FBFD;
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(0, 186, 218, 0.2);
        }
        .mss-chat-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          height: 16px;
          min-width: 16px;
          padding: 0 4px;
          border-radius: 999px;
          background: #EF4444;
          color: #FFFFFF;
          font-size: 10.5px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #FFFFFF;
          box-shadow: 0 2px 4px rgba(239, 68, 68, 0.4);
        }

        .mss-profile-btn {
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 100%);
          border: none;
          border-radius: 999px;
          color: #FFFFFF;
          padding: 7px 16px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.1px;
          box-shadow: 0 2px 10px rgba(0, 186, 218, 0.35);
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
        }
        .mss-profile-btn:hover {
          background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(0, 186, 218, 0.5);
        }

        /* Bottom CTA Button */
        .mss-cta-btn {
          width: 100%;
          height: 48px;
          border-radius: 999px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 750;
          letter-spacing: 0.2px;
          color: #FFFFFF;
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 50%, #0088B3 100%);
          box-shadow: 0 6px 22px rgba(0, 186, 218, 0.4);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
        }
        .mss-cta-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 50%, #0099C8 100%);
          box-shadow: 0 8px 26px rgba(0, 186, 218, 0.55);
          transform: translateY(-1.5px);
        }
        .mss-cta-btn:active:not(:disabled) {
          transform: translateY(0.5px);
          box-shadow: 0 4px 14px rgba(0, 186, 218, 0.28);
        }
        .mss-cta-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          box-shadow: none;
        }
      `}</style>
      <div
        className={`mss-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="position-relative" style={{ padding: "26px 24px 28px" }}>
          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="mss-close-btn"
            aria-label="Close modal"
          >
            <IoClose size={18} />
          </button>

          {/* Header */}
          <h5
            style={{
              fontWeight: 800,
              fontSize: "21px",
              color: "#0F1A2B",
              margin: "0 0 4px",
              paddingRight: "36px",
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
            }}
          >
            {isDurationCompleted
              ? lang?.mentorship_session_completed_title || "Session Duration Completed"
              : isInProgress
              ? lang?.mentorship_session_in_progress_title || "Session in Progress"
              : isConfirmed
              ? lang?.mentorship_scheduled_session_title || "Scheduled Session"
              : lang?.mentorship_schedule_session_title || "Schedule your session"}
          </h5>
          <p
            style={{
              color: "#475569",
              fontSize: "12.5px",
              fontWeight: 500,
              margin: "0 0 18px",
              lineHeight: 1.5,
            }}
          >
            {isDurationCompleted
              ? lang?.mentorship_session_completed_subtitle || "This session's duration has ended. Please mark it as complete."
              : isInProgress
              ? lang?.mentorship_session_live_subtitle || "This mentorship session is currently live — join it below."
              : isConfirmed
              ? lang?.mentorship_session_already_scheduled_subtitle || "Session already scheduled — update the date or time if needed."
              : lang?.mentorship_pick_date_time_subtitle || "Pick a date and time — the Zoom link will be generated automatically."}
          </p>

          {/* MENTEE ACCOUNT DETAILS */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <Users3DIcon size={16} />
            <span
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "#0891B2",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {lang?.mentorship_mentee_account_details_label || "MENTEE ACCOUNT DETAILS"}
            </span>
          </div>

          <div className="mss-mentee-card d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, zIndex: 1 }}>
              <div
                className="mss-avatar-wrap"
                onClick={handleGoToProfile}
                style={{ cursor: "pointer" }}
                title={lang?.mentorship_go_to_profile_button || "Go to Profile"}
              >
                <img
                  src={resolveMentorImage(mentee.image)}
                  alt={mentee.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = placeholderAvatar;
                  }}
                  className="mss-avatar-img"
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <p
                  className="text-truncate mb-0"
                  style={{
                    fontWeight: 750,
                    fontSize: "14.5px",
                    color: "#0F1A2B",
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                    cursor: "pointer",
                  }}
                  title={mentee.name}
                  onClick={handleGoToProfile}
                >
                  {mentee.name}
                </p>
                <p
                  className="text-truncate mb-0"
                  style={{
                    fontSize: "12px",
                    color: "#64748B",
                    fontWeight: 500,
                    marginTop: "2px",
                  }}
                  title={mentorshipSubtitle(mentee, lang)}
                >
                  {mentorshipSubtitle(mentee, lang)}
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ zIndex: 1 }}>
              <button
                type="button"
                onClick={handleGoToChat}
                className="mss-chat-btn position-relative"
                title="Chat with Mentee"
              >
                <MdOutlineSms size={16} color="#0891B2" />
                {mentee.statusCount ? (
                  <span className="mss-chat-badge">
                    {mentee.statusCount}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={handleGoToProfile}
                className="mss-profile-btn"
              >
                {lang?.mentorship_go_to_profile_button || "Go to Profile"}
              </button>
            </div>
          </div>

          {/* CV — only shown when this request actually has one attached */}
          {mentee.cvUrl && (
            <div
              className="d-flex align-items-center justify-content-between mb-3"
              style={{
                background: "linear-gradient(145deg, #F0FDF4 0%, #DCFCE7 100%)",
                border: "1.5px solid #86EFAC",
                borderRadius: "16px",
                padding: "10px 14px",
                boxShadow: "0 2px 8px rgba(21, 128, 61, 0.08)",
              }}
            >
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: "#FFFFFF",
                    border: "1px solid #86EFAC",
                    color: "#15803D",
                    boxShadow: "0 2px 6px rgba(21, 128, 61, 0.12)",
                  }}
                >
                  <PiFilePdf size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <span
                    className="d-block text-truncate"
                    style={{ color: "#14532D", fontWeight: 700, fontSize: "13.5px" }}
                    title={mentee.cvFileName}
                  >
                    {mentee.cvFileName}
                  </span>
                  <span className="d-block" style={{ color: "#15803D", fontWeight: 500, fontSize: "11.5px" }}>
                    {lang?.mentorship_pdf_format || "PDF format"}
                  </span>
                </div>
              </div>
              <a
                href={mentee.cvUrl}
                target="_blank"
                rel="noreferrer"
                className="d-flex align-items-center gap-1 flex-shrink-0 text-decoration-none"
                style={{
                  background: "#FFFFFF",
                  color: "#15803D",
                  borderRadius: "999px",
                  padding: "6px 14px",
                  border: "1.5px solid #86EFAC",
                  fontWeight: 700,
                  fontSize: "12px",
                  boxShadow: "0 2px 6px rgba(21, 128, 61, 0.1)",
                  transition: "all 0.2s ease",
                }}
              >
                <IoOpenOutline size={14} /> {lang?.mentorship_open_link || "Open"}
              </a>
            </div>
          )}

          {/* SCHEDULE SESSION */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <Clock3DIcon size={16} />
            <span
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "#0891B2",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {lang?.mentorship_schedule_session_label || "SCHEDULE SESSION"}
            </span>
          </div>

          <div className="row gx-3 gy-3 mb-3">
            <div className="col-6">
              <span
                className="d-block mb-1"
                style={{ color: "#334155", fontWeight: 700, fontSize: "12.5px" }}
              >
                {lang?.date || "Date"}
              </span>
              <ModernDatePicker
                value={date}
                minDate={moment().startOf("day")}
                disabled={isDurationCompleted}
                onChange={(newValue) => {
                  setDate(newValue);
                  setErrors((prev) => ({ ...prev, date: "" }));
                }}
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                error={Boolean(errors.date)}
              />
              {errors.date && (
                <p className="fs-11 mb-0 mt-1" style={{ color: "#EF4444", fontWeight: 500 }}>
                  {errors.date}
                </p>
              )}
            </div>
            <div className="col-6">
              <span
                className="d-block mb-1"
                style={{ color: "#334155", fontWeight: 700, fontSize: "12.5px" }}
              >
                {lang?.start_time || "Start Time"}
              </span>
              <ModernTimePicker
                value={startTime}
                disabled={isDurationCompleted}
                onChange={(newValue) => {
                  setStartTime(newValue);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                placeholder={lang?.mentorship_select_time_placeholder || "Select your time"}
                error={Boolean(errors.startTime)}
                relatedDate={date}
                minTime={
                  date && date.isSame(moment(), "day")
                    ? moment().add(10, "minutes")
                    : undefined
                }
              />
              {errors.startTime && (
                <p className="fs-11 mb-0 mt-1" style={{ color: "#EF4444", fontWeight: 500 }}>
                  {errors.startTime}
                </p>
              )}
            </div>
          </div>

          <div className="row gx-3 gy-3 mb-3">
            <div className="col-6">
              <span
                className="d-block mb-1"
                style={{ color: "#334155", fontWeight: 700, fontSize: "12.5px" }}
              >
                {lang?.mentorship_duration_label || "Duration"}
              </span>
              <FieldSelect
                value={duration}
                onChange={(value) => {
                  setDuration(value);
                  setErrors((prev) => ({ ...prev, duration: "" }));
                }}
                options={durationOptions}
                placeholder={lang?.mentorship_select_duration_placeholder || "Select duration"}
                error={errors.duration}
                disabled={isDurationCompleted}
              />
            </div>
            <div className="col-6">
              <span
                className="d-block mb-1"
                style={{ color: "#334155", fontWeight: 700, fontSize: "12.5px" }}
              >
                {lang?.mentorship_timezone_label || "Timezone"}
              </span>
              <FieldSelect
                value={timezone}
                onChange={(value) => {
                  setTimezone(value);
                  setErrors((prev) => ({ ...prev, timezone: "" }));
                }}
                options={TIMEZONE_OPTIONS}
                placeholder={lang?.mentorship_select_timezone_placeholder || "Select timezone"}
                error={errors.timezone}
                disabled={isDurationCompleted}
              />
            </div>
          </div>

          {/* Notify via chat */}
          {!isConfirmed && !isInProgress && (
            <div
              style={{
                borderTop: "1px solid #E2E8F0",
                paddingTop: "14px",
                marginTop: "4px",
              }}
              className="mb-4"
            >
              <div
                className="d-flex align-items-center"
                onClick={() => {
                  setNotifyViaChat((prev) => !prev);
                  setErrors((prev) => ({ ...prev, notifyViaChat: "" }));
                }}
                style={{ cursor: "pointer", gap: "10px" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "6px",
                    border: notifyViaChat
                      ? "none"
                      : `1.5px solid ${errors.notifyViaChat ? "#EF4444" : "#CBD5E1"}`,
                    background: notifyViaChat
                      ? "linear-gradient(135deg, #00C8E8 0%, #00BADA 100%)"
                      : "#FFFFFF",
                    boxShadow: notifyViaChat
                      ? "0 2px 8px rgba(0, 186, 218, 0.35)"
                      : "none",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                  }}
                >
                  {notifyViaChat && (
                    <svg width="11" height="9" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <p
                  className="mb-0"
                  style={{
                    color: "#475569",
                    fontWeight: 500,
                    fontSize: "12.5px",
                    lineHeight: 1.4,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {lang?.mentorship_notify_via_chat_desc ||
                    "Send this Zoom link and session details to the mentee automatically via chat."}
                </p>
              </div>
              {errors.notifyViaChat && (
                <p className="fs-11 mb-0 mt-1" style={{ color: "#EF4444", fontWeight: 500 }}>
                  {errors.notifyViaChat}
                </p>
              )}
            </div>
          )}

          {/* Zoom link — once a session is confirmed or already live */}
          {(isConfirmed || isInProgress) && !isDurationCompleted && (
            <div
              className="d-flex align-items-center justify-content-between mb-4"
              style={{
                background: "linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%)",
                border: "1.5px solid #B4E8F2",
                borderRadius: "16px",
                padding: "10px 16px",
                boxShadow: "0 2px 10px rgba(0, 186, 218, 0.08)",
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                <img src={zoom} alt="Zoom" style={{ width: "28px", height: "28px" }} />
                <div style={{ minWidth: 0 }}>
                  <span
                    className="d-block text-truncate"
                    style={{ color: "#0F1A2B", fontWeight: 750, fontSize: "13.5px" }}
                  >
                    {isInProgress
                      ? lang?.mentorship_session_live_label || "Session is live"
                      : lang?.mentorship_zoom_ready_label || "Zoom session ready"}
                  </span>
                  <span
                    className="d-block text-truncate"
                    style={{ color: "#64748B", fontWeight: 500, fontSize: "11.5px" }}
                  >
                    {lang?.mentorship_join_via_zoom_desc || "Join the session via Zoom"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!mentee.meetingUrl) {
                    toast.error(
                      lang?.mentorship_zoom_link_unavailable || "The Zoom link isn't available yet."
                    );
                    return;
                  }
                  window.open(mentee.meetingUrl, "_blank", "noopener,noreferrer");
                }}
                className="d-flex align-items-center border-0 text-white flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg, #00C8E8 0%, #00BADA 100%)",
                  borderRadius: "999px",
                  fontWeight: 700,
                  fontSize: "12.5px",
                  padding: "7px 16px",
                  gap: "7px",
                  boxShadow: "0 2px 10px rgba(0, 186, 218, 0.35)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <LiaVideoSolid size={17} />
                <span>{lang?.mentorship_join_button || "Join"}</span>
              </button>
            </div>
          )}

          {/* Bottom CTA Button */}
          {isDurationCompleted ? (
            <button
              type="button"
              onClick={handleCompleteSession}
              disabled={completing}
              className="mss-cta-btn"
            >
              {completing
                ? lang?.mentorship_completing_button || "Completing…"
                : lang?.mentorship_complete_session_button || "Complete Session"}
              {!completing && <FaArrowRight size={14} />}
            </button>
          ) : (
            !isInProgress && (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting || isRescheduleUnchanged}
                className="mss-cta-btn"
              >
                {submitting
                  ? lang?.mentorship_scheduling_button || "Scheduling…"
                  : isConfirmed
                  ? lang?.mentorship_reschedule_button || "Reschedule Session"
                  : lang?.mentorship_confirm_send_button || "Confirm & Send to Mentee"}
                {!submitting && <FaArrowRight size={14} />}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(MentorShipSchedule);